import React from 'react';
import { getReturnActNumber } from '../../utils/attachmentNumbers';
import { formatDateContract } from '../../utils/dateFormat';
import InteractiveField from '../common/InteractiveField';
import DateField from '../common/DateField';

/**
 * Компонент "Акт возврата жилого помещения"
 * Номер и дата договора синхронизированы с данными договора (как в других актах).
 * Остальные поля заполняются РУЧКОЙ при подписании.
 * Номер приложения вычисляется динамически: 2, если наличный расчет не выбран, иначе 3
 */
const ReturnActSection = ({ contractData, onChange, showValidation = false }) => {
    const {
        header = {},
        parties = {},
        property = {},
        term = {},
        payment = {},
        attachments = {}
    } = contractData || {};

    const returnAct = attachments?.returnAct || {};
    const { securityDeposit: mainContractSecurityPayment = {} } = payment;
    const isEditable = typeof onChange === 'function';

    const handleHeaderChange = (field, value) => {
        if (onChange) {
            onChange({
                ...contractData,
                header: { ...header, [field]: value }
            });
        }
    };

    const handlePartiesChange = (field, value) => {
        if (!onChange) return;

        const newParties = { ...parties, [field]: value };
        const signatures = contractData.signatures || { landlord: {}, tenant: {} };
        const updated = {
            ...contractData,
            parties: newParties
        };

        // Двусторонняя синхронизация паспортных данных с разделом "Адреса и реквизиты Сторон"
        if (field === 'landlordPassportSerial' || field === 'landlordPassportNumber') {
            updated.signatures = {
                ...signatures,
                landlord: {
                    ...signatures.landlord,
                    passportSerial: field === 'landlordPassportSerial' ? value : newParties.landlordPassportSerial,
                    passportNumber: field === 'landlordPassportNumber' ? value : newParties.landlordPassportNumber
                }
            };
        }

        if (field === 'tenantPassportSerial' || field === 'tenantPassportNumber') {
            updated.signatures = {
                ...signatures,
                tenant: {
                    ...signatures.tenant,
                    passportSerial: field === 'tenantPassportSerial' ? value : newParties.tenantPassportSerial,
                    passportNumber: field === 'tenantPassportNumber' ? value : newParties.tenantPassportNumber
                }
            };
        }

        onChange(updated);
    };

    const handlePropertyChange = (field, value) => {
        if (onChange) {
            onChange({
                ...contractData,
                property: { ...property, [field]: value }
            });
        }
    };

    const handlePropertyToggle = (field) => {
        if (onChange) {
            onChange({
                ...contractData,
                property: { ...property, [field]: !property[field] }
            });
        }
    };

    // Функция для автоматического определения количества экземпляров
    const getAutomaticCopiesCount = () => {
        const durationMonths = term.durationMonths || 0;
        return durationMonths >= 12 ? '3' : '2';
    };

    const automaticCopiesCount = getAutomaticCopiesCount();

    // Получаем номер приложения динамически
    const appendixNumber = getReturnActNumber(contractData);

    // Данные из акта приемки (показываем только то, что было указано при приемке)
    const rawHandoverItems = property.handoverItems || {};
    const keysAndDocuments = rawHandoverItems.keysAndDocuments || {};
    const meterReadings = rawHandoverItems.meterReadings || {};
    
    // Проверяем, что было включено в акте приемки
    const hasKeysInHandover = keysAndDocuments.enabled;
    const hasDocumentationInHandover = keysAndDocuments.documentation;
    const hasMeterReadingsInHandover = meterReadings.enabled;

    return (
        <div className="document-preview-editor return-act" style={{ marginTop: '60px', pageBreakBefore: 'always' }}>
            {/* Информационная надпись */}
            <p className="document-preamble" style={{ fontStyle: 'italic', color: 'var(--text-secondary)', background: 'var(--bg-subtle)', padding: '8px', borderRadius: '8px', marginBottom: '16px' }}>
                Внимание! Акт возврата заполняется вручную на бумаге при возврате квартиры. Бланк скачается вместе с договором.
            </p>

            {/* Заголовок приложения */}
            <div style={{ textAlign: 'right', marginBottom: '20px', fontSize: '14px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <div style={{ margin: '0 0 5px 0' }}>Приложение № {appendixNumber}</div>
                <div style={{ margin: '0 0 5px 0' }}>
                    к Договору найма жилого помещения № {isEditable ? (
                        <InteractiveField
                            value={header.number}
                            onChange={(v) => handleHeaderChange('number', v)}
                            placeholder="5716"
                            isRequired={true}
                            showValidation={showValidation}
                        />
                    ) : (header.number || '_____')}
                </div>
                <div style={{ margin: '0' }}>
                    от {isEditable ? (
                        <>
                            <DateField
                                value={header.date}
                                onChange={(v) => handleHeaderChange('date', v)}
                                placeholder="дд.мм.гггг"
                                isRequired={true}
                                showValidation={showValidation}
                            /> г.
                        </>
                    ) : (header.date ? formatDateContract(header.date) : '«__» _______ 20__ г.')}
                </div>
            </div>

            {/* Название акта */}
            <div className="document-title-block">
                <h3>Акт</h3>
                <p>возврата жилого помещения.</p>
            </div>

            {/* Место и дата: город синхронизируется с договором, дата — статичная полоска для ручного заполнения при подписании */}
            <div className="document-location-date">
                {isEditable ? (
                    <InteractiveField
                        value={header.city}
                        onChange={(v) => handleHeaderChange('city', v)}
                        placeholder="г. Москва"
                        isRequired={true}
                        showValidation={showValidation}
                    />
                ) : (
                    <span>{header.city || 'г. __________'}</span>
                )}
                <span>{returnAct.date ? formatDateContract(returnAct.date) : '«__» ________ _____ г.'}</span>
            </div>

            {/* Преамбула с данными сторон из договора */}
            <p className="document-preamble">
                Гражданин РФ {isEditable ? (
                    <InteractiveField
                        value={parties.landlordName}
                        onChange={(v) => handlePartiesChange('landlordName', v)}
                        placeholder="Ф.И.О. полностью"
                        isRequired={true}
                        showValidation={showValidation}
                    />
                ) : (parties.landlordName || '________________')},
                паспорт серии {isEditable ? (
                    <InteractiveField
                        value={parties.landlordPassportSerial}
                        onChange={(v) => handlePartiesChange('landlordPassportSerial', v)}
                        placeholder="4512"
                        isRequired={true}
                        showValidation={showValidation}
                        className="passport-serial-mask"
                    />
                ) : (parties.landlordPassportSerial || '____')} № {isEditable ? (
                    <InteractiveField
                        value={parties.landlordPassportNumber}
                        onChange={(v) => handlePartiesChange('landlordPassportNumber', v)}
                        placeholder="654321"
                        isRequired={true}
                        showValidation={showValidation}
                        className="passport-number-mask"
                    />
                ) : (parties.landlordPassportNumber || '______')},
                именуемый(ая) в дальнейшем "Наймодатель", с одной стороны и Гражданин РФ {isEditable ? (
                    <InteractiveField
                        value={parties.tenantName}
                        onChange={(v) => handlePartiesChange('tenantName', v)}
                        placeholder="Ф.И.О. полностью"
                        isRequired={true}
                        showValidation={showValidation}
                    />
                ) : (parties.tenantName || '________________')},
                паспорт серии {isEditable ? (
                    <InteractiveField
                        value={parties.tenantPassportSerial}
                        onChange={(v) => handlePartiesChange('tenantPassportSerial', v)}
                        placeholder="4509"
                        isRequired={true}
                        showValidation={showValidation}
                        className="passport-serial-mask"
                    />
                ) : (parties.tenantPassportSerial || '____')} № {isEditable ? (
                    <InteractiveField
                        value={parties.tenantPassportNumber}
                        onChange={(v) => handlePartiesChange('tenantPassportNumber', v)}
                        placeholder="123456"
                        isRequired={true}
                        showValidation={showValidation}
                        className="passport-number-mask"
                    />
                ) : (parties.tenantPassportNumber || '______')},
                именуемый(ая) в дальнейшем "Наниматель", с другой стороны, согласно условиям Договора найма жилого помещения № {isEditable ? (
                    <InteractiveField
                        value={header.number}
                        onChange={(v) => handleHeaderChange('number', v)}
                        placeholder="5716"
                        isRequired={true}
                        showValidation={showValidation}
                    />
                ) : (header.number || '_____')} от {isEditable ? (
                    <DateField
                        value={header.date}
                        onChange={(v) => handleHeaderChange('date', v)}
                        placeholder="дд.мм.гггг"
                        isRequired={true}
                        showValidation={showValidation}
                        compact
                    />
                ) : (header.date ? formatDateContract(header.date).replace(/ г\.$/, '') : '<число> <месяц> <год>')} (далее - Договор) составили настоящий Акт о нижеследующем:
            </p>

            {/* Пункт 1 */}
            <p className="document-preamble">
                1. Наниматель возвратил жилое помещение, квартиру (далее - Помещение), а Наймодатель принял обратно Помещение.
            </p>

            {/* Пункт 2 - Описание помещения из договора */}
            <p className="document-preamble">
                2. Помещение располагается в многоквартирном доме по адресу: {isEditable ? (
                    <InteractiveField
                        value={property.address}
                        onChange={(v) => handlePropertyChange('address', v)}
                        placeholder="г. Москва, ул. Центральная, д. 10, кв. 55"
                        isRequired={true}
                        showValidation={showValidation}
                    />
                ) : (property.address || '______________________________________')}, в {isEditable ? (
                    <InteractiveField
                        value={property.buildingFloors}
                        onChange={(v) => handlePropertyChange('buildingFloors', v)}
                        placeholder="5"
                        type="number"
                        showValidation={showValidation}
                    />
                ) : (property.buildingFloors || '__')}-этажном доме на {isEditable ? (
                    <InteractiveField
                        value={property.apartmentFloor}
                        onChange={(v) => handlePropertyChange('apartmentFloor', v)}
                        placeholder="3"
                        type="number"
                        showValidation={showValidation}
                    />
                ) : (property.apartmentFloor || '__')}-м этаже.
            </p>

            <p className="document-preamble">
                Параметры Помещения:
            </p>
            <p className="document-preamble">
                квартира: {isEditable ? (
                    <InteractiveField
                        value={property.roomCount}
                        onChange={(v) => handlePropertyChange('roomCount', v)}
                        placeholder="2"
                        type="number"
                        isRequired={true}
                        showValidation={showValidation}
                    />
                ) : (property.roomCount || '__')}-комнатная;
            </p>
            <p className="document-preamble">
                общая площадь {isEditable ? (
                    <InteractiveField
                        value={property.totalArea}
                        onChange={(v) => handlePropertyChange('totalArea', v)}
                        placeholder="55.5"
                        type="number"
                        isRequired={true}
                        showValidation={showValidation}
                    />
                ) : (property.totalArea || '___')} кв. м;
            </p>

            {isEditable && (
                <div className="optional-field-block dynamic-content-block active-green">
                    <div className="option-pills-group" style={{ marginBottom: '16px' }}>
                        <button
                            className={`option-pill-toggle ${property.includeLivingArea ? 'selected' : ''}`}
                            onClick={() => handlePropertyToggle('includeLivingArea')}
                        >
                            {property.includeLivingArea ? '✓ Жилая площадь' : '+ Добавить жилую площадь'}
                        </button>
                    </div>
                    {property.includeLivingArea && (
                        <p className="document-preamble">
                            жилая площадь <InteractiveField
                                value={property.livingArea}
                                onChange={(v) => handlePropertyChange('livingArea', v)}
                                placeholder="35.0"
                                type="number"
                                isRequired={true}
                                showValidation={showValidation}
                            /> кв. м;
                        </p>
                    )}
                </div>
            )}
            {!isEditable && property.includeLivingArea && property.livingArea && (
                <p className="document-preamble">
                    жилая площадь {property.livingArea} кв. м;
                </p>
            )}

            {isEditable && (
                <div className="optional-field-block dynamic-content-block active-green">
                    <div className="option-pills-group" style={{ marginBottom: '16px' }}>
                        <button
                            className={`option-pill-toggle ${property.includeCadastralNumber ? 'selected' : ''}`}
                            onClick={() => handlePropertyToggle('includeCadastralNumber')}
                        >
                            {property.includeCadastralNumber ? '✓ Кадастровый номер' : '+ Добавить кадастровый номер'}
                        </button>
                    </div>
                    {property.includeCadastralNumber && (
                        <p className="document-preamble">
                            кадастровый номер квартиры: <InteractiveField
                                value={property.cadastralNumber}
                                onChange={(v) => handlePropertyChange('cadastralNumber', v)}
                                placeholder="77:01:0001001:1234"
                                isRequired={true}
                                showValidation={showValidation}
                            />;
                        </p>
                    )}
                </div>
            )}
            {!isEditable && property.includeCadastralNumber && property.cadastralNumber && (
                <p className="document-preamble">
                    кадастровый номер квартиры: {property.cadastralNumber};
                </p>
            )}

            {isEditable && (
                <div className="optional-field-block dynamic-content-block active-green">
                    <div className="option-pills-group" style={{ marginBottom: '16px' }}>
                        <button
                            className={`option-pill-toggle ${property.includeOtherParameters ? 'selected' : ''}`}
                            onClick={() => handlePropertyToggle('includeOtherParameters')}
                        >
                            {property.includeOtherParameters ? '✓ Иные параметры' : '+ Добавить иные параметры'}
                        </button>
                    </div>
                    {property.includeOtherParameters && (
                        <p className="document-preamble">
                            иные параметры помещения: <InteractiveField
                                value={property.otherParameters}
                                onChange={(v) => handlePropertyChange('otherParameters', v)}
                                placeholder="описание"
                                isRequired={true}
                                showValidation={showValidation}
                            />;
                        </p>
                    )}
                </div>
            )}
            {!isEditable && property.includeOtherParameters && property.otherParameters && (
                <p className="document-preamble">
                    иные параметры помещения: {property.otherParameters};
                </p>
            )}

            {/* Пункт 3 - Результат приемки возврата - заполняется вручную */}
            <p className="document-preamble" style={{ marginTop: '16px' }}>
                3. Возвращаемое имущество, оборудование и Помещение передано:
            </p>
            
            <p className="document-preamble" style={{ marginLeft: '20px', marginTop: '8px' }}>
                <span style={{ fontStyle: 'italic' }}>Отметить галочкой нужное:</span>
            </p>
            
            <p className="document-preamble" style={{ marginLeft: '20px', marginTop: '8px' }}>
                ☐ в надлежащем состоянии и количестве, Наймодатель претензий к Нанимателю не имеет;
            </p>
            
            <p className="document-preamble" style={{ marginLeft: '20px', marginTop: '8px' }}>
                ☐ с недостатками: _______________________________________________________________
            </p>
            <p className="document-preamble" style={{ marginLeft: '20px' }}>
                ________________________________________________________________________________
            </p>
            
            <p className="document-preamble" style={{ marginLeft: '40px', marginTop: '8px', fontStyle: 'italic', fontSize: '14px' }}>
                Сроки устранения недостатков Нанимателем: в течение ____ дней с момента подписания настоящего акта.
            </p>

            {/* Пункт 4 - Возврат ключей и документации (только если были в акте приемки) */}
            {(hasKeysInHandover || hasDocumentationInHandover) && (
                <div className="">
                    <p className="document-preamble" style={{ marginTop: '16px' }}>
                        4. Наймодатель также с Помещением возвращает:
                    </p>
                    {hasKeysInHandover && (
                            <p className="document-preamble">
                            ключи: в ____ экземпляр(е/ах);
                            </p>
                        )}
                    {hasDocumentationInHandover && (
                                <p className="document-preamble">
                            документацию: ____________________________________________________________________
                        </p>
                    )}
                </div>
            )}

            {/* Пункт 5 - Показания приборов учета (только если были в акте приемки) */}
            {hasMeterReadingsInHandover && (
                <div className="">
                    <p className="document-preamble" style={{ marginTop: '16px' }}>
                        {(hasKeysInHandover || hasDocumentationInHandover) ? '5' : '4'}. Показания приборов учета:
                    </p>
                    {meterReadings.electricity && (
                            <p className="document-preamble">
                            электроэнергия: _____________ прибор учета № {meterReadings.electricity.number || '___________'};
                            </p>
                        )}
                    {meterReadings.hotWater && (
                            <p className="document-preamble">
                            горячая вода: _____________ прибор учета № {meterReadings.hotWater.number || '___________'};
                            </p>
                        )}
                    {meterReadings.coldWater && (
                            <p className="document-preamble">
                            холодная вода: _____________ прибор учета № {meterReadings.coldWater.number || '___________'};
                            </p>
                        )}
                    {meterReadings.gas && (
                            <p className="document-preamble">
                            газ: _____________ прибор учета № {meterReadings.gas.number || '___________'}.
                            </p>
                        )}
                </div>
            )}

            {/* Динамическая нумерация для возврата обеспечительного платежа и заключительных положений */}
            {(() => {
                let currentNumber = 4; // Начинаем с 4 (пункты 1-3 всегда есть)
                
                // Если были ключи или документация - увеличиваем номер
                if (hasKeysInHandover || hasDocumentationInHandover) {
                    currentNumber++;
                }
                
                // Если были приборы учета - увеличиваем номер
                if (hasMeterReadingsInHandover) {
                    currentNumber++;
                }
                
                const securityPaymentNumber = currentNumber;
                // Пункт о возврате платежа показывается только если он предусмотрен И не засчитывается в счет последнего месяца
                const shouldShowSecurityPayment = mainContractSecurityPayment.enabled && mainContractSecurityPayment.returnType !== 'credit';
                const finalProvisionsNumber = shouldShowSecurityPayment ? currentNumber + 1 : currentNumber;
                
                return (
                    <>
                        {/* Возврат обеспечительного платежа (если был и не засчитывается в счет последнего месяца) */}
            {mainContractSecurityPayment.enabled && mainContractSecurityPayment.returnType !== 'credit' && (
                            <div className="">
                            <p className="document-preamble" style={{ marginTop: '16px' }}>
                                {securityPaymentNumber}. Наймодатель вернул обеспечительный платеж в размере{' '}
                                <span style={{ display: 'inline-block', minWidth: '120px' }}>________________</span>{' '}
                                рублей, Наниматель претензий не имеет.
                </p>
                </div>
            )}

                        {/* Заключительные положения */}
            {automaticCopiesCount === '3' ? (
                            <p className="document-preamble" style={{ marginTop: '20px' }}>
                                {finalProvisionsNumber}. Настоящий Акт составлен в трех экземплярах, имеющих равную юридическую силу, по одному для Сторон и один - для регистрирующего органа.
                </p>
            ) : (
                            <p className="document-preamble" style={{ marginTop: '20px' }}>
                                {finalProvisionsNumber}. Настоящий Акт составлен в двух экземплярах, имеющих равную юридическую силу, по одному для каждой из Сторон.
                </p>
            )}

            <p className="document-preamble">
                            {finalProvisionsNumber + 1}. Настоящий Акт является неотъемлемой частью Договора.
            </p>
                    </>
                );
            })()}
        </div>
    );
};

export default ReturnActSection;
